import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  type ActiveAdvancedFilterState,
  type AdvancedFilterState,
  type RootState,
  type TCodeData,
} from '../../../shared/types';
import {
  type AppliedColumnFilters,
  type AuditLogColumnFilters,
} from '../AuditLogColumnFilter';
import {
  type AuditLogSearchResponse,
  type LaunchDetail,
  type RequestMetadata,
  type ReviewActivity,
  type ReviewDetailInfo,
  type ReviewState,
} from '../data-layer/auditLogTypes';
import { DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS } from '../data-layer/auditLogUtils';
import { type TimelineData } from '../TimeLineBlock';

import type { AuditLogState } from './auditLogState.types';

export type { AuditLogState } from './auditLogState.types';

// Initial state
const initialState: AuditLogState = {
  timelineData: [],
  originalTimelineData: [],
  totalRecords: 0,
  searchAfter: null,
  isLoading: false,
  error: null,
  // Persona
  isReadOnly: false,
  requestMetadata: null,
  requestReviewSummary: null,
  reviewState: null,
  launchDetail: [],
  isLoadingRequestReviewDetails: false,
  requestReviewDetailsError: null,
  exportMaxDateRangeMonths: DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS,
  fetchedColumns: null,
  isColumnsLoading: false,
  searchQuery: '',
  filters: [],
  activeFilters: [],
  activeEntitlementFilters: [],
  columnFilters: [],
  appliedColumnFilters: {},
  auditLogViewerControls: {
    activeFilters: [],
    appliedColumnFilters: {},
    searchQuery: '',
  },
  insightIds: [],
  suppressInitialFetch: false,
  reviewActivitiesByLogId: {},
  isPostingReview: false,
  postReviewError: null,
  completeReviewError: null,
  activeTab: 'all-logs',
  openTransactionId: null,
  carryOverAuditStatus: null,
  insights: [],
  isTCodeListExpanded: false,
};

// Create slice
const auditLogSlice = createSlice({
  name: 'auditLog',
  initialState,
  reducers: {
    // Set timeline data
    setTimelineData: (
      state,
      action: PayloadAction<{
        data: TimelineData[];
        totalRecords: number;
        searchAfter?: (string | number)[] | null;
      }>
    ) => {
      state.timelineData = action.payload.data;
      state.totalRecords = action.payload.totalRecords;

      if (action.payload.searchAfter !== undefined) {
        // Set searchAfter only if it's a valid cursor (not null/empty)
        state.searchAfter =
          action.payload.searchAfter &&
          Array.isArray(action.payload.searchAfter) &&
          action.payload.searchAfter.length > 0
            ? action.payload.searchAfter
            : null;
      }

      state.error = null;
    },

    // Save full search response (timeline, totals, request metadata, readOnly)
    setSearchResult: (state, action: PayloadAction<AuditLogSearchResponse>) => {
      state.timelineData = action.payload.data;
      state.totalRecords = action.payload.totalRecords;
      // Set searchAfter only if it's a valid cursor (not null/empty)
      state.searchAfter =
        action.payload.searchAfter &&
        Array.isArray(action.payload.searchAfter) &&
        action.payload.searchAfter.length > 0
          ? action.payload.searchAfter
          : null;
      state.error = null;
    },
    setRequestReviewDetails: (
      state,
      action: PayloadAction<{
        requestMetadata: RequestMetadata | null;
        isReadOnly: boolean;
        reviewSummary: ReviewDetailInfo | null;
        state: ReviewState;
        launchDetail: LaunchDetail[];
        exportMaxDateRangeMonths: number;
      }>
    ) => {
      state.requestMetadata = action.payload.requestMetadata;
      state.isReadOnly = action.payload.isReadOnly;
      state.requestReviewSummary = action.payload.reviewSummary;
      state.reviewState = action.payload.state;
      state.launchDetail = Array.isArray(action.payload.launchDetail)
        ? action.payload.launchDetail
        : [];
      state.exportMaxDateRangeMonths = action.payload.exportMaxDateRangeMonths;
      state.isLoadingRequestReviewDetails = false;
      state.requestReviewDetailsError = null;
    },

    setLoadingRequestReviewDetails: (state, action: PayloadAction<boolean>) => {
      state.isLoadingRequestReviewDetails = action.payload;

      if (action.payload) {
        state.requestReviewDetailsError = null;
      }
    },

    setRequestReviewDetailsError: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.requestReviewDetailsError = action.payload;
      state.isLoadingRequestReviewDetails = false;

      if (action.payload) {
        state.exportMaxDateRangeMonths =
          DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS;
      }
    },

    setFetchedColumns: (state, action: PayloadAction<unknown>) => {
      state.fetchedColumns = action.payload;
      state.isColumnsLoading = false;
    },

    setColumnsLoading: (state, action: PayloadAction<boolean>) => {
      state.isColumnsLoading = action.payload;
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;

      // Only stop loading when setting an actual error.
      // Many call sites dispatch setError(null) to clear an old error before starting a request.
      if (action.payload) {
        state.isLoading = false;
      }
    },

    // Persona and request metadata are set via setSearchResult only

    // Set search query
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      // Keep combined controls in sync
      state.auditLogViewerControls = {
        activeFilters: state.activeFilters,
        appliedColumnFilters: state.appliedColumnFilters,
        searchQuery: state.searchQuery,
      };
    },
    setInsightIds: (state, action: PayloadAction<string[]>) => {
      state.insightIds = action.payload;
    },
    applyInsightFilter: (state, action: PayloadAction<string | null>) => {
      // Set/clear insight filter and atomically reset pagination/loading
      state.insightIds = action.payload ? [action.payload] : [];
      state.suppressInitialFetch = true;
      state.isLoading = true;
      state.searchAfter = null;
      state.timelineData = [];
      state.totalRecords = 0;
      state.error = null;
    },
    setSuppressInitialFetch: (state, action: PayloadAction<boolean>) => {
      state.suppressInitialFetch = action.payload;
    },

    // UI: set active tab for context ('all-logs' | 'transaction-overview')
    setActiveTab: (
      state,
      action: PayloadAction<'all-logs' | 'transaction-overview'>
    ) => {
      state.activeTab = action.payload;
    },

    // Filters controls
    setFilters: (state, action: PayloadAction<AdvancedFilterState[]>) => {
      state.filters = action.payload;
    },
    setActiveFilters: (
      state,
      action: PayloadAction<ActiveAdvancedFilterState[]>
    ) => {
      state.activeFilters = action.payload;
      // Keep combined controls in sync
      state.auditLogViewerControls = {
        activeFilters: state.activeFilters,
        appliedColumnFilters: state.appliedColumnFilters,
        searchQuery: state.searchQuery,
      };
    },
    // Set entitlement filters (transaction-overview)
    setActiveEntitlementFilters: (
      state,
      action: PayloadAction<ActiveAdvancedFilterState[]>
    ) => {
      state.activeEntitlementFilters = action.payload;
    },

    // Column picker controls
    setColumnFilters: (state, action: PayloadAction<AuditLogColumnFilters>) => {
      state.columnFilters = action.payload;
    },
    setAppliedColumnFilters: (
      state,
      action: PayloadAction<AppliedColumnFilters>
    ) => {
      state.appliedColumnFilters = action.payload;
      // Keep combined controls in sync
      state.auditLogViewerControls = {
        activeFilters: state.activeFilters,
        appliedColumnFilters: state.appliedColumnFilters,
        searchQuery: state.searchQuery,
      };
    },
    // Optional direct setter if needed
    setAuditLogViewerControls: (
      state,
      action: PayloadAction<{
        activeFilters: ActiveAdvancedFilterState[];
        appliedColumnFilters: AppliedColumnFilters;
        searchQuery: string;
      }>
    ) => {
      state.auditLogViewerControls = action.payload;
      state.activeFilters = action.payload.activeFilters;
      state.appliedColumnFilters = action.payload.appliedColumnFilters;
      state.searchQuery = action.payload.searchQuery;
    },

    // Clear all data
    clearData: (state) => {
      state.timelineData = [];
      state.totalRecords = 0;
      state.searchAfter = null;
      state.error = null;
      state.isLoading = false;
    },

    // Reset to initial state
    resetAuditLog: () => initialState,

    // Insights
    setInsights: (state, action: PayloadAction<TCodeData[]>) => {
      state.insights = action.payload;
    },
    clearInsights: (state) => {
      state.insights = [];
    },

    // Review activities
    setReviewActivities: (
      state,
      action: PayloadAction<{
        uniqueLogId: string | number;
        activities: ReviewActivity[];
      }>
    ) => {
      const { uniqueLogId, activities } = action.payload;

      state.reviewActivitiesByLogId[uniqueLogId] = activities;

      // Also recompute and update commentsCount on the timeline items so the badge updates immediately
      const commentedCount = Array.isArray(activities)
        ? activities.filter((a) => a.status === 'COMMENTED').length
        : 0;

      const idx = state.timelineData.findIndex(
        (t) => String(t.uniqueLogId) === String(uniqueLogId)
      );

      if (idx !== -1) {
        state.timelineData[idx].commentsCount = commentedCount;
      }

      const idxOrig = state.originalTimelineData.findIndex(
        (t) => String(t.uniqueLogId) === String(uniqueLogId)
      );

      if (idxOrig !== -1) {
        state.originalTimelineData[idxOrig].commentsCount = commentedCount;
      }
    },
    // Set latest activity for a timeline item from activities array
    setTimelineItemLatestActivity: (
      state,
      action: PayloadAction<{
        uniqueLogId: string | number;
        activities: ReviewActivity[];
      }>
    ) => {
      const { uniqueLogId, activities } = action.payload;
      const idx = state.timelineData.findIndex(
        (t) => String(t.uniqueLogId) === String(uniqueLogId)
      );

      // Hoisted typed helpers used in both branches
      const getTs = (v: ReviewActivity['updatedAt']): number => {
        if (typeof v === 'number') {
          return v;
        }

        if (v instanceof Date) {
          return v.getTime();
        }

        const t = Date.parse(String(v));

        return Number.isFinite(t) ? t : 0;
      };

      const acts: ReviewActivity[] = Array.isArray(activities)
        ? activities
        : [];

      if (idx !== -1) {
        // Pick most recent activity by updatedAt (fallback to last item)

        const latest: ReviewActivity | undefined = acts.length
          ? acts.reduce(
              (acc: ReviewActivity, curr: ReviewActivity) =>
                getTs(curr.updatedAt) >= getTs(acc.updatedAt) ? curr : acc,
              acts[0]
            )
          : undefined;

        state.timelineData[idx].latestActivity = latest
          ? {
              status: latest.status,
              updatedBy: latest.user,
              comment: latest.comment,
              updatedAt:
                typeof latest.updatedAt === 'string' ||
                typeof latest.updatedAt === 'number' ||
                latest.updatedAt instanceof Date
                  ? latest.updatedAt
                  : undefined,
            }
          : null;
      }

      // Keep originalTimelineData in sync
      const idxOrig = state.originalTimelineData.findIndex(
        (t) => String(t.uniqueLogId) === String(uniqueLogId)
      );

      if (idxOrig !== -1) {
        const latest: ReviewActivity | undefined = acts.length
          ? acts.reduce(
              (acc: ReviewActivity, curr: ReviewActivity) =>
                getTs(curr.updatedAt) >= getTs(acc.updatedAt) ? curr : acc,
              acts[0]
            )
          : undefined;

        state.originalTimelineData[idxOrig].latestActivity = latest
          ? {
              status: latest.status,
              updatedBy: latest.user,
              comment: latest.comment,
              updatedAt:
                typeof latest.updatedAt === 'string' ||
                typeof latest.updatedAt === 'number' ||
                latest.updatedAt instanceof Date
                  ? latest.updatedAt
                  : undefined,
            }
          : null;
      }
    },
    setIsPostingReview: (state, action: PayloadAction<boolean>) => {
      state.isPostingReview = action.payload;
    },
    setPostReviewError: (state, action: PayloadAction<string | null>) => {
      state.postReviewError = action.payload;
    },
    setCompleteReviewError: (state, action: PayloadAction<string | null>) => {
      state.completeReviewError = action.payload;
    },
    // Update a single timeline item's rowLogStatus
    setTimelineItemStatus: (
      state,
      action: PayloadAction<{
        uniqueLogId: string | number;
        rowLogStatus: string;
      }>
    ) => {
      const { uniqueLogId, rowLogStatus } = action.payload;
      const idx = state.timelineData.findIndex(
        (t) => String(t.uniqueLogId) === String(uniqueLogId)
      );

      if (idx !== -1) {
        state.timelineData[idx].rowLogStatus = rowLogStatus;
      }
    },
    // Transaction Overview: set/clear open transaction row id
    setOpenTransactionId: (state, action: PayloadAction<string | null>) => {
      state.openTransactionId = action.payload;
    },
    // Set carry-over audit status for Smart Filters -> All Logs navigation
    setCarryOverAuditStatus: (state, action: PayloadAction<string | null>) => {
      state.carryOverAuditStatus = action.payload;
    },
    // Reset searchAfter cursor (for filter changes)
    resetSearchAfter: (state) => {
      state.searchAfter = null;
    },
    // Update comment count for a timeline item
    updateTimelineItemCommentCount: (
      state,
      action: PayloadAction<{
        uniqueLogId: string | number;
        commentsCount: number;
      }>
    ) => {
      const { uniqueLogId, commentsCount } = action.payload;
      const idx = state.timelineData.findIndex(
        (t) => String(t.uniqueLogId) === String(uniqueLogId)
      );

      if (idx !== -1) {
        state.timelineData[idx].commentsCount = commentsCount;
      }

      // Keep originalTimelineData in sync for views that source from it
      const idxOrig = state.originalTimelineData.findIndex(
        (t) => String(t.uniqueLogId) === String(uniqueLogId)
      );

      if (idxOrig !== -1) {
        state.originalTimelineData[idxOrig].commentsCount = commentsCount;
      }
    },

    // Set original unfiltered data
    setOriginalTimelineData: (state, action: PayloadAction<TimelineData[]>) => {
      state.originalTimelineData = action.payload;
    },
    // T-code list expanded state
    setTCodeListExpanded: (state, action: PayloadAction<boolean>) => {
      state.isTCodeListExpanded = action.payload;
    },
  },
});

// Export actions
export const auditLogActions = auditLogSlice.actions;

// Export reducer
export default auditLogSlice.reducer;

// Export selectors
export const selectAuditLogState = (state: {
  aag: { auditLog: AuditLogState };
}) => state.aag.auditLog;

export const selectTimelineData = (state: {
  aag: { auditLog: AuditLogState };
}) => state.aag.auditLog.timelineData;

export const selectIsLoading = (state: { aag: { auditLog: AuditLogState } }) =>
  state.aag.auditLog.isLoading;

export const selectError = (state: { aag: { auditLog: AuditLogState } }) =>
  state.aag.auditLog.error;

export const selectSearchQuery = (state: {
  aag: { auditLog: AuditLogState };
}) => state.aag.auditLog.searchQuery;

export const selectIsReadOnly = (state: { aag: { auditLog: AuditLogState } }) =>
  state.aag.auditLog.isReadOnly;

export const selectRequestMetadata = (state: {
  aag: { auditLog: AuditLogState };
}) => state.aag.auditLog.requestMetadata;

export const selectTotalRecords = (state: {
  aag: { auditLog: AuditLogState };
}) => state.aag.auditLog.totalRecords;

export const selectAuditFilters = (state: {
  aag: { auditLog: AuditLogState };
}) => state.aag.auditLog.filters;

export const selectAuditActiveFilters = (state: {
  aag: { auditLog: AuditLogState };
}) => state.aag.auditLog.activeFilters;

export const selectReviewActivitiesByLogId = (
  state: { aag: { auditLog: AuditLogState } },
  uniqueLogId: string | number
) => state.aag.auditLog.reviewActivitiesByLogId[uniqueLogId] ?? [];

export const selectIsPostingReview = (state: {
  aag: { auditLog: AuditLogState };
}) => state.aag.auditLog.isPostingReview;

export const selectPostReviewError = (state: {
  aag: { auditLog: AuditLogState };
}) => state.aag.auditLog.postReviewError;

// Memoized selector-like helper to derive active insight name
export const selectActiveInsightName = (state: RootState): string => {
  const { insightIds, insights } = (
    state.aag as unknown as { auditLog: AuditLogState }
  ).auditLog;
  const id =
    Array.isArray(insightIds) && insightIds.length > 0 ? insightIds[0] : null;

  if (!id) {
    return '';
  }

  const list = Array.isArray(insights) ? insights : [];
  const found = list.find((i) => String(i.id) === String(id));

  return found &&
    typeof (found as { description?: unknown }).description === 'string'
    ? (found as { description: string }).description
    : '';
};
